import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { TabGroup } from "@/components/common/TabGroup";
import MarketingMetrics from "@/components/dashboard/MarketingMetrics";
import ConversionMetrics from "@/components/dashboard/ConversionMetrics";
import SalesMetrics from "@/components/dashboard/SalesMetrics";
import PipelineMetricsRPC from "@/components/dashboard/PipelineMetricsRPC";
import CaseMetrics from "@/components/dashboard/CaseMetrics";
import FinanceMetrics from "@/components/dashboard/FinanceMetrics";
import FacebookAdsDashboard from "@/components/dashboard/FacebookAdsDashboard";
import SLAMetrics from "@/components/dashboard/SLAMetrics";
import CallsMetrics from "@/components/dashboard/CallsMetrics";
import ChannelsOriginMetrics from "@/components/dashboard/ChannelsOriginMetrics";
import ReferralsMetrics from "@/components/dashboard/ReferralsMetrics";
import { ReferralCallMetrics } from "@/components/dashboard/ReferralCallMetrics";
import { UsageMetrics } from "@/components/dashboard/UsageMetrics";
import { SDRConnectionsSection } from "@/components/dashboard/SDRConnectionsSection";
import { BusinessUnitSelector } from "@/components/finance/BusinessUnitSelector";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format, subDays, startOfYear, startOfMonth, endOfMonth, subMonths } from "date-fns";
import { Calendar as CalendarIcon, Loader2, Building2 } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  getLocalDayStart,
  getLocalDayEnd,
  getLocalWeekStart,
  getLocalMonthStart,
  getDaysAgo,
} from "@/lib/timezoneUtils";
import { Label } from "@/components/ui/label";

// Define which tabs need which data
const TAB_DATA_REQUIREMENTS = {
  sales: [],      // ✅ Uses RPC - no lazy loading needed
  pipeline: ["users"],   // Needs users for consultant/SDR filters
  channels: ["deals", "users"],
  marketing: ["leads", "deals"],
  finance: ["deals"],
  cases: ["cases"],
  sla: ["cases"],
  // These tabs have their own hooks and don't need central data
  calls: [],      // ✅ Uses RPC - no lazy loading needed
  "sdr-connections": [],
  referrals: [],
  "referral-calls": [],
  usage: [],
  facebook: [],
  operations: [],
};

export default function Admin() {
  const { t } = useTranslation();
  const { user } = useAuth();

  // Data states
  const [cases, setCases] = useState([]);
  const [leads, setLeads] = useState([]);
  const [deals, setDeals] = useState([]);
  const [users, setUsers] = useState([]);

  // Loading states for each data type (lazy loading)
  const [loadingStates, setLoadingStates] = useState({
    deals: false,
    users: false,
    cases: false,
    leads: false,
  });

  // Track which data has been loaded
  const [loadedData, setLoadedData] = useState({
    deals: false,
    users: false,
    cases: false,
    leads: false,
  });

  const [activeTab, setActiveTab] = useState("sales");
  const [dateRange, setDateRange] = useState(() => {
    const now = new Date();
    return {
      from: getLocalMonthStart(now),
      to: getLocalDayEnd(now),
    };
  });
  const [quickFilter, setQuickFilter] = useState("month");
  const [businessUnitFilter, setBusinessUnitFilter] = useState("all");

  // Pipeline filters
  const [pipelineConsultantFilter, setPipelineConsultantFilter] = useState("all");
  const [pipelineSdrFilter, setPipelineSdrFilter] = useState("all");
  const [pipelineSourceFilter, setPipelineSourceFilter] = useState("all");
  const [pipelineChannelFilter, setPipelineChannelFilter] = useState("all");
  const [pipelineCreatedDateFilter, setPipelineCreatedDateFilter] = useState("all");
  const [pipelineCreatedDateRange, setPipelineCreatedDateRange] = useState(null);
  const [pipelineClosingDateRange, setPipelineClosingDateRange] = useState(null);

  // Individual data loaders
  const fetchAllDeals = useCallback(async () => {
    const BATCH_SIZE = 1000;
    let allDeals = [];
    let offset = 0;
    let hasMore = true;

    while (hasMore) {
      const { data, error } = await supabase
        .from("deals")
        .select(
          `
          *,
          contact:contacts!fk_deals_contact(id, first_name, last_name),
          sales_consultant_user:app_users!sales_consultant(id, display_name, first_name, last_name),
          sales_development_user:app_users!sales_development(id, display_name, first_name, last_name),
          payments(id, status, amount, paid_amount)
        `,
        )
        .range(offset, offset + BATCH_SIZE - 1)
        .order("created_at", { ascending: false });

      if (error) throw error;

      allDeals = [...allDeals, ...(data || [])];
      hasMore = data?.length === BATCH_SIZE;
      offset += BATCH_SIZE;
    }

    return allDeals;
  }, []);

  const loadDeals = useCallback(async () => {
    if (loadedData.deals) return;

    setLoadingStates((prev) => ({ ...prev, deals: true }));
    try {
      const dealsData = await fetchAllDeals();
      console.log("[Admin] 📊 Deals carregados (lazy):", dealsData.length);
      setDeals(dealsData);
      setLoadedData((prev) => ({ ...prev, deals: true }));
    } catch (error) {
      console.error("Error loading deals:", error);
    } finally {
      setLoadingStates((prev) => ({ ...prev, deals: false }));
    }
  }, [loadedData.deals, fetchAllDeals]);

  const loadUsers = useCallback(async () => {
    if (loadedData.users) return;

    setLoadingStates((prev) => ({ ...prev, users: true }));
    try {
      const { data, error } = await supabase.from("app_users").select("*, system_role:system_roles(id, code, name)");

      if (error) throw error;
      setUsers(data || []);
      setLoadedData((prev) => ({ ...prev, users: true }));
    } catch (error) {
      console.error("Error loading users:", error);
    } finally {
      setLoadingStates((prev) => ({ ...prev, users: false }));
    }
  }, [loadedData.users]);

  const loadCases = useCallback(async () => {
    if (loadedData.cases) return;

    setLoadingStates((prev) => ({ ...prev, cases: true }));
    try {
      const { data, error } = await supabase.from("cases").select("*").limit(50000);

      if (error) throw error;
      setCases(data || []);
      setLoadedData((prev) => ({ ...prev, cases: true }));
    } catch (error) {
      console.error("Error loading cases:", error);
    } finally {
      setLoadingStates((prev) => ({ ...prev, cases: false }));
    }
  }, [loadedData.cases]);

  const loadLeads = useCallback(async () => {
    if (loadedData.leads) return;

    setLoadingStates((prev) => ({ ...prev, leads: true }));
    try {
      const { data, error } = await supabase.from("leads").select("*").limit(50000);

      if (error) throw error;
      setLeads(data || []);
      setLoadedData((prev) => ({ ...prev, leads: true }));
    } catch (error) {
      console.error("Error loading leads:", error);
    } finally {
      setLoadingStates((prev) => ({ ...prev, leads: false }));
    }
  }, [loadedData.leads]);

  // Load data based on active tab
  useEffect(() => {
    if (!user) return;

    const requirements = TAB_DATA_REQUIREMENTS[activeTab] || [];

    const loadRequiredData = async () => {
      const loaders = [];

      if (requirements.includes("deals") && !loadedData.deals) {
        loaders.push(loadDeals());
      }
      if (requirements.includes("users") && !loadedData.users) {
        loaders.push(loadUsers());
      }
      if (requirements.includes("cases") && !loadedData.cases) {
        loaders.push(loadCases());
      }
      if (requirements.includes("leads") && !loadedData.leads) {
        loaders.push(loadLeads());
      }

      if (loaders.length > 0) {
        await Promise.all(loaders);
      }
    };

    loadRequiredData();
  }, [user, activeTab, loadedData, loadDeals, loadUsers, loadCases, loadLeads]);

  const handleQuickFilter = (filter) => {
    setQuickFilter(filter);
    const now = new Date();

    switch (filter) {
      case "today":
        setDateRange({
          from: getLocalDayStart(now),
          to: getLocalDayEnd(now),
        });
        break;
      case "yesterday":
        const yesterday = subDays(now, 1);
        setDateRange({
          from: getLocalDayStart(yesterday),
          to: getLocalDayEnd(yesterday),
        });
        break;
      case "7days":
        setDateRange({
          from: getDaysAgo(7),
          to: getLocalDayEnd(now),
        });
        break;
      case "week":
        setDateRange({
          from: getLocalWeekStart(now),
          to: getLocalDayEnd(now),
        });
        break;
      case "30days":
        setDateRange({
          from: getDaysAgo(30),
          to: getLocalDayEnd(now),
        });
        break;
      case "month":
        setDateRange({
          from: getLocalMonthStart(now),
          to: getLocalDayEnd(now),
        });
        break;
      case "lastMonth":
        const lastMonth = subMonths(now, 1);
        setDateRange({
          from: startOfMonth(lastMonth),
          to: endOfMonth(lastMonth),
        });
        break;
      case "year":
        const yearStart = startOfYear(now);
        setDateRange({
          from: getLocalDayStart(yearStart),
          to: getLocalDayEnd(now),
        });
        break;
      case "all":
        setDateRange({ from: null, to: null });
        break;
      default:
        setDateRange({
          from: getDaysAgo(30),
          to: getLocalDayEnd(now),
        });
    }
  };

  const filterByDateRange = (items, dateField = "created_at") => {
    if (!dateRange || !dateRange.from || !dateRange.to) return items;

    return items.filter((item) => {
      if (!item[dateField]) return false;
      const itemDate = new Date(item[dateField]);
      return itemDate >= dateRange.from && itemDate <= dateRange.to;
    });
  };

  // Filter deals by business unit
  const filterDealsByBusinessUnit = (dealsToFilter, buFilter) => {
    if (buFilter === "all") return dealsToFilter;
    return dealsToFilter.filter((deal) => deal.business_unit === buFilter);
  };

  const getFilteredData = () => {
    return {
      leads: filterByDateRange(leads),
      deals: filterByDateRange(deals),
    };
  };

  // Check if current tab is loading
  const isTabLoading = () => {
    const requirements = TAB_DATA_REQUIREMENTS[activeTab] || [];
    return requirements.some((req) => loadingStates[req] || (!loadedData[req] && requirements.includes(req)));
  };

  // Check if current tab has all required data
  const hasRequiredData = () => {
    const requirements = TAB_DATA_REQUIREMENTS[activeTab] || [];
    if (requirements.length === 0) return true;
    return requirements.every((req) => loadedData[req]);
  };

  // Deals filtered by business unit for sales, pipeline, channels tabs
  const dealsByBusinessUnit = filterDealsByBusinessUnit(deals, businessUnitFilter);

  const tabs = [
    { id: "sales", label: t("admin.tabs.sales") },
    { id: "pipeline", label: t("admin.tabs.pipeline") },
    { id: "channels", label: t("admin.tabs.channelsOrigin", "Canais & Origem") },
    { id: "marketing", label: t("admin.tabs.marketing") },
    { id: "calls", label: t("admin.tabs.calls") },
    { id: "sdr-connections", label: "Conexões SDR" },
    { id: "referrals", label: t("admin.tabs.referrals", "Indicações") },
    { id: "referral-calls", label: "Calls de Indicação" },
    { id: "usage", label: "Uso do Sistema" },
    { id: "cases", label: t("admin.tabs.cases") },
    { id: "finance", label: t("admin.tabs.finance") },
    { id: "operations", label: t("admin.tabs.operations") },
    { id: "facebook", label: t("admin.tabs.facebook") },
    { id: "sla", label: t("admin.tabs.sla") },
  ];

  const quickFilters = [
    { id: "today", label: t("admin.filters.today") },
    { id: "yesterday", label: t("admin.filters.yesterday") },
    { id: "7days", label: t("admin.filters.sevenDays") },
    { id: "week", label: t("admin.filters.thisWeek") },
    { id: "30days", label: t("admin.filters.thirtyDays") },
    { id: "month", label: t("admin.filters.thisMonth") },
    { id: "lastMonth", label: t("admin.filters.lastMonth") },
    { id: "year", label: t("admin.filters.thisYear") },
    { id: "all", label: t("admin.filters.allTime") },
  ];

  const filteredData = getFilteredData();
  const loading = isTabLoading();
  const dataReady = hasRequiredData();

  return (
    <div className="p-6 space-y-6">
      <TabGroup tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

      {(activeTab === "sales" ||
        activeTab === "marketing" ||
        activeTab === "finance" ||
        activeTab === "calls" ||
        activeTab === "sdr-connections" ||
        activeTab === "referrals" ||
        activeTab === "referral-calls" ||
        activeTab === "usage") && (
        <div className="flex flex-wrap gap-4 items-center bg-card p-4 rounded-lg border">
          <div className="flex flex-1 justify-between items-center gap-4">
            <div className="flex gap-2 flex-wrap">
              {quickFilters.map((filter) => (
                <Button
                  key={filter.id}
                  variant={quickFilter === filter.id ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleQuickFilter(filter.id)}
                >
                  {filter.label}
                </Button>
              ))}
            </div>

            <div className="flex items-center gap-4">
              {/* Business Unit Filter - for Sales tab */}
              {activeTab === "sales" && (
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <BusinessUnitSelector
                    value={businessUnitFilter}
                    onChange={setBusinessUnitFilter}
                    includeAll={true}
                    className="w-[160px]"
                  />
                </div>
              )}

              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn("justify-start text-left font-normal", !dateRange && "text-muted-foreground")}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateRange?.from ? (
                      dateRange.to ? (
                        <>
                          {format(dateRange.from, "dd/MM/yyyy")} - {format(dateRange.to, "dd/MM/yyyy")}
                        </>
                      ) : (
                        format(dateRange.from, "dd/MM/yyyy")
                      )
                    ) : (
                      <span>{t("admin.filters.selectPeriod")}</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <Calendar
                    initialFocus
                    mode="range"
                    defaultMonth={dateRange?.from}
                    selected={dateRange}
                    onSelect={setDateRange}
                    numberOfMonths={2}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>
      )}

      {/* Business Unit filter for Pipeline and Channels tabs */}
      {(activeTab === "pipeline" || activeTab === "channels") && (
        <div className="flex items-center gap-4 bg-card p-4 rounded-lg border">
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-muted-foreground" />
            <Label className="text-sm text-muted-foreground">Unidade:</Label>
            <BusinessUnitSelector
              value={businessUnitFilter}
              onChange={setBusinessUnitFilter}
              includeAll={true}
              className="w-[160px]"
            />
          </div>
        </div>
      )}

      {loading && !dataReady ? (
        <div className="flex flex-col items-center justify-center py-20 space-y-4">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
          <p className="text-muted-foreground">{t("admin.loading")}</p>
          <p className="text-xs text-muted-foreground">{t("admin.loadingLarge")}</p>
        </div>
      ) : (
        <div className="space-y-6">
          {activeTab === "marketing" && (
            <>
              <MarketingMetrics leads={filteredData.leads} deals={filteredData.deals} dateRange={dateRange} />
              <ConversionMetrics leads={filteredData.leads} deals={filteredData.deals} />
            </>
          )}

          {activeTab === "sales" && (
            <SalesMetrics
              dateRange={dateRange}
              businessUnit={businessUnitFilter}
            />
          )}

          {activeTab === "pipeline" && (
            <PipelineMetricsRPC
              users={users}
              consultantFilter={pipelineConsultantFilter}
              onConsultantFilterChange={setPipelineConsultantFilter}
              sdrFilter={pipelineSdrFilter}
              onSdrFilterChange={setPipelineSdrFilter}
              sourceFilter={pipelineSourceFilter}
              onSourceFilterChange={setPipelineSourceFilter}
              channelFilter={pipelineChannelFilter}
              onChannelFilterChange={setPipelineChannelFilter}
              createdDateFilter={pipelineCreatedDateFilter}
              onCreatedDateFilterChange={setPipelineCreatedDateFilter}
              createdDateRange={pipelineCreatedDateRange}
              onCreatedDateRangeChange={setPipelineCreatedDateRange}
              businessUnitFilter={businessUnitFilter}
            />
          )}

          {activeTab === "channels" && <ChannelsOriginMetrics deals={dealsByBusinessUnit} users={users} />}

          {activeTab === "calls" && <CallsMetrics dateRange={dateRange} />}

          {activeTab === "cases" && <CaseMetrics cases={cases} />}

          {activeTab === "finance" && <FinanceMetrics deals={deals} dateRange={dateRange} />}

          {activeTab === "operations" && (
            <div className="text-center py-12 text-muted-foreground">{t("admin.operationsInDevelopment")}</div>
          )}

          {activeTab === "facebook" && <FacebookAdsDashboard />}

          {activeTab === "sla" && <SLAMetrics cases={cases} />}

          {activeTab === "referrals" && <ReferralsMetrics dateRange={dateRange} />}

          {activeTab === "referral-calls" && <ReferralCallMetrics dateRange={dateRange} />}

          {activeTab === "usage" && <UsageMetrics dateRange={dateRange} />}

          {activeTab === "sdr-connections" && <SDRConnectionsSection dateRange={dateRange} />}
        </div>
      )}
    </div>
  );
}
